import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled, { useTheme } from 'styled-components';
import DashboardNavbar from '../dashboardNavbar';
import {
  Card, StatGrid, Stat, StatTop, StatIco, StatLabel, StatValue,
  SetTabs, SetTab, TblToolbar, CellPrimary, CellSub, RowIco, CellFlex, Av,
  Tag, TagDot, StatusBadge, MiniProg, Chip, Btn, ChartGrid, Grid2, Tile, KpiIco, Muted, Skeleton,
} from '../ui/primitives';
import DataTable from '../ui/DataTable';
import GroupedRowActionSheet, { ICON_PROPS } from '../ui/GroupedRowActionSheet';
import {
  ASSESSMENT_SORT_FIELDS,
  COURSE_DETAIL_LIST_DEFAULTS,
  COURSE_TRAINING_SORT_FIELDS,
  ENROLLEE_SORT_FIELDS,
  getNextCourseDetailSort,
  sortAssessmentsLocally,
  sortCourseTrainingsLocally,
  sortEnrolleesLocally,
} from './courseDetailListApi';
import {
  Sparkles, ClipboardList, Pencil, Trash2, Settings, Video, Eye, Download, Plus,
} from 'lucide-react';
import { Icon, Donut } from '../ui/shell';
import { LabelWithHelp } from '../ui/FieldHelp';
import { COURSE_DETAIL_KPI_HELP } from '../ui/kpiFieldHelp';
import { COURSE_DETAIL_TABLE_HELP as TABLE_HELP } from './courseDetailTableFieldHelp';
import { COURSE_DETAIL_TRAINING_TABLE_HELP as TRAINING_TABLE_HELP } from '../ui/listScreenFieldHelp';
import UploadPDF from './UploadPDF';
import AssessmentDialog from './assessmentdialog';
import CourseEditDialog from './dialog';
import AssessmentFormDialog from '../assignment/dialog';
import AssessmentSettingDialog from '../assignment/AssessmentSettingDialog';
import StudentFormDialog from '../student/dialog';
import GenerateQuestiondialog from '../assignment/GenerateQuestiondialog';
import TrainingVideoDialog from '../training/TrainingVideoDialog';
import CourseAnalysis from './CourseAnalysis';
import CourseProgressCell from './CourseProgressCell';
import { getCache, setCache } from '../ui/listCache';
import { useConfirm } from '../ui/ConfirmProvider';
import PageActionsMenu, {
  ActionsMenuItem,
  ActionsSection,
} from '../ui/PageActionsMenu';
import { useAppContext } from '../../appprovider';
import {
  canUseAiCourseBuilder,
  aiCourseBuilderGateBody,
} from '../services/instructorMeterLimits';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';
import { isFeatureHidden, isFeatureDisabled } from '../services/demoPolicy';

/* ============================================================
   Course detail workspace — drill-in from the Courses list.
   1:1 with the reference CourseDetail: hero + KPI cards +
   Overview / Assessments / Enrollees / Materials tabs.

   Data comes from a single GET call by courseId:
     GET {API}/api/course/details/{courseId}
   expected payload (see CourseDetail.payloadShape below).
   ============================================================ */

const Hero = styled(Card)`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.padCard};

  .hero__title { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .hero__title h2 { font-family: ${({ theme }) => theme.fontHead}; font-size: 22px; font-weight: 700; margin: 0; letter-spacing: -.02em; }
  .hero__sub { font-size: ${({ theme }) => theme.fsSm}; color: ${({ theme }) => theme.textMuted}; margin-top: 5px; }
  .hero__chips { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
  .hero__actions { display: flex; justify-content: flex-end; flex-wrap: wrap; }
`;

const SectionTitle = styled.h3`
  font-family: ${({ theme }) => theme.fontHead};
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 700;
`;

const MetaGrid = styled(Grid2)`
  margin-top: 18px;
  gap: 14px;
`;

const MetaCell = styled.div`
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme }) => theme.surface2};

  .meta__k { font-size: ${({ theme }) => theme.fsMicro}; color: ${({ theme }) => theme.textMuted}; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
  .meta__v { font-weight: 650; margin-top: 4px; font-size: 14px; }
`;

const UploadTile = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 2px dashed ${({ theme }) => theme.borderStrong};
  border-radius: ${({ theme }) => theme.rLg};
  background: ${({ theme }) => theme.surface2};
  color: ${({ theme }) => theme.accentStrong};
  font-weight: 650;
  padding: ${({ theme }) => theme.padCard};
  cursor: pointer;
`;

const ShimmerWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.gap};
`;

const STATUS_TAG = {
  active: ['green', 'Active'],
  completed: ['accent', 'Completed'],
  invited: ['gray', 'Invited'],
  'at-risk': ['red', 'At risk'],
  inactive: ['gray', 'Inactive'],
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = /^\d+$/.test(String(v)) ? new Date(parseInt(v, 10)) : new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const initials = (name) => String(name || '?').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';
const studentInitial = { courseId: '', studentId: '', courseName: '', firstName: '', lastName: '', studentEmail: '' };

const CourseDetail = () => {
  const askConfirmation = useConfirm();
  const navigate = useNavigate();
  const { state } = useLocation();
  const theme = useTheme();
  const apiUrl = process.env.REACT_APP_API_URL;
  const { entitlements, openUpgradeGate } = useAppContext();
  const { isLocked, guardAction, lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const aiBuilderEnabled = canUseAiCourseBuilder(entitlements);
  const analysisHidden =
    isFeatureHidden(entitlements, 'nav.yaki')
    || isFeatureHidden(entitlements, 'action.yaki.analyzeCourse');
  const analysisLocked =
    isFeatureDisabled(entitlements, 'nav.yaki')
    || isLocked('action.yaki.analyzeCourse');

  const openGenerateForAssessment = (assessmentRow) => {
    if (!aiBuilderEnabled) {
      openUpgradeGate(aiCourseBuilderGateBody(entitlements));
      return;
    }
    setGenerateAssessment(assessmentRow);
    setGenerateOpen(true);
  };
  const passed = (state && state.course) || {};
  const courseId = passed.courseId ?? passed.id ?? (state && state.courseId);

  const initialTab = (state && state.tab) || ((state && state.scope === 'course') ? 'analysis' : 'overview');
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [savingStudent, setSavingStudent] = useState(false);
  const [data, setData] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [assessmentCreateOpen, setAssessmentCreateOpen] = useState(false);
  const [assessmentEditOpen, setAssessmentEditOpen] = useState(false);
  // Per-assessment settings dialog (shared AssessmentSettingForm).
  const [assessmentSettingsOpen, setAssessmentSettingsOpen] = useState(false);
  const [assessmentSettingsTarget, setAssessmentSettingsTarget] = useState(null);
  // Edit-course dialog (distinct from "Edit settings" which opens the settings page).
  const [editCourseOpen, setEditCourseOpen] = useState(false);
  const [editCourseData, setEditCourseData] = useState(null);
  const [editCourseLoading, setEditCourseLoading] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  // Generate-questions dialog (same one used on the Assessments page).
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateAssessment, setGenerateAssessment] = useState(null);
  const [studentOpen, setStudentOpen] = useState(false);
  const [studentData, setStudentData] = useState(studentInitial);
  // Materials tab — search + add-by-URL (re-analyze hooks documented below).
  const [materialSearch, setMaterialSearch] = useState('');
  const [materialUrl, setMaterialUrl] = useState('');
  // Training "Watch video" — play inline in a dialog (no new tab / download).
  const [trainingVideoOpen, setTrainingVideoOpen] = useState(false);
  const [trainingVideoUrl, setTrainingVideoUrl] = useState(null);

  const [assessmentSortBy, setAssessmentSortBy] = useState(COURSE_DETAIL_LIST_DEFAULTS.sortBy);
  const [assessmentSortDir, setAssessmentSortDir] = useState(COURSE_DETAIL_LIST_DEFAULTS.sortDir);
  const [enrolleeSortBy, setEnrolleeSortBy] = useState(COURSE_DETAIL_LIST_DEFAULTS.sortBy);
  const [enrolleeSortDir, setEnrolleeSortDir] = useState(COURSE_DETAIL_LIST_DEFAULTS.sortDir);
  const [trainingSortBy, setTrainingSortBy] = useState(COURSE_DETAIL_LIST_DEFAULTS.sortBy);
  const [trainingSortDir, setTrainingSortDir] = useState(COURSE_DETAIL_LIST_DEFAULTS.sortDir);
  const [gradebookSortBy, setGradebookSortBy] = useState(COURSE_DETAIL_LIST_DEFAULTS.sortBy);
  const [gradebookSortDir, setGradebookSortDir] = useState(COURSE_DETAIL_LIST_DEFAULTS.sortDir);

  const handleAssessmentSortChange = (columnKey) => {
    const next = getNextCourseDetailSort(columnKey, assessmentSortBy, assessmentSortDir);
    setAssessmentSortBy(next.sortBy);
    setAssessmentSortDir(next.sortDir);
  };
  const handleEnrolleeSortChange = (columnKey) => {
    const next = getNextCourseDetailSort(columnKey, enrolleeSortBy, enrolleeSortDir);
    setEnrolleeSortBy(next.sortBy);
    setEnrolleeSortDir(next.sortDir);
  };
  const handleTrainingSortChange = (columnKey) => {
    const next = getNextCourseDetailSort(columnKey, trainingSortBy, trainingSortDir);
    setTrainingSortBy(next.sortBy);
    setTrainingSortDir(next.sortDir);
  };
  const handleGradebookSortChange = (columnKey) => {
    const next = getNextCourseDetailSort(columnKey, gradebookSortBy, gradebookSortDir);
    setGradebookSortBy(next.sortBy);
    setGradebookSortDir(next.sortDir);
  };

  // Add a learning material by URL.
  //   POST {API}/api/course/material/url   body: { courseId, url }
  const handleAddMaterialUrl = () => {
    if (!guardAction('action.course.detail.material.addUrl')) return;
    if (!materialUrl.trim()) return;
    alert('Add material by URL — coming soon (API integration pending).');
    setMaterialUrl('');
  };

  // Re-run AI analysis on a single material.
  //   POST {API}/api/course/material/analyze   body: { courseId, materialId }
  const handleReanalyzeMaterial = () => {
    if (!guardAction('action.course.detail.material.reanalyze')) return;
    alert('Re-analyze material — coming soon (API integration pending).');
  };

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    return `Basic ${btoa(`${username}:${password}`)}`;
  };

  const fetchDetails = useCallback(async (showLoader = true) => {
    if (!courseId) { setLoading(false); return; }
    if (showLoader) setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/api/course/details/${courseId}`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });
      setData(res.data);
      setCache(`course-details:${courseId}`, res.data);
    } catch (err) {
      console.error('Error fetching course details:', err);
      setData(null);
    } finally {
      if (showLoader) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, courseId]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!alive) return;
      // Cache-first: reuse the previously-loaded course bundle so navigating
      // away and back doesn't re-hit the API. It's refreshed only when data is
      // added/updated (every mutation calls fetchDetails again, which re-caches).
      const cached = courseId ? getCache(`course-details:${courseId}`) : null;
      if (cached) { setData(cached); setLoading(false); }
      else { await fetchDetails(true); }
    };
    load();
    return () => { alive = false; };
  }, [fetchDetails, courseId]);

  // Merge fetched payload with the row passed in (fallback for header info).
  const course = { ...passed, ...((data && data.course) || {}) };
  const resolvedCourseId = course.courseId || courseId;
  const assessments = (data && (data.assessments || data.assessment || data.tests)) || course.assessments || [];
  const enrollees = (data && (data.enrollees || data.students || data.studentCourses)) || course.enrollees || [];
  const materials = (data && (data.materials || data.material || data.pdfs || data.files)) || course.materials || [];
  const trainings = (data && data.trainings) || course.trainings || [];
  const courseDomains = course.domains || [];

  // "Edit course" — opens the course edit form (name/code/vendor/domains),
  // distinct from "Edit settings" which opens the settings page.
  const openEditCourse = () => {
    if (!guardAction('action.course.detail.editCourse')) return;
    setEditCourseData({
      ...course,
      domains: (course.domains || []).map((d) => ({
        ...d,
        subDomain: (d.subDomain || []).map((s) => ({ ...s })),
      })),
    });
    setEditCourseOpen(true);
  };

  const onEditCourseChange = (e) => {
    const { id, value } = e.target;
    setEditCourseData((prev) => ({ ...prev, [id]: value }));
  };

  const handleEditCourseSubmit = async () => {
    if (!editCourseData) return;
    if (!guardAction('action.course.detail.editCourse')) return;
    const userId = localStorage.getItem('userId');
    setEditCourseLoading(true);
    try {
      await axios.put(
        `${apiUrl}/api/course/${userId}&${editCourseData.courseId}`,
        editCourseData,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            Authorization: getBasicAuthHeader(),
          },
        },
      );
      setEditCourseOpen(false);
      fetchDetails(true); // refresh the detail view + cache
    } catch (err) {
      console.error('Error updating course:', err);
    } finally {
      setEditCourseLoading(false);
    }
  };

  const studentsCount = course.students ?? enrollees.length;
  const graded = enrollees.filter((s) => Number(s.score) > 0);
  const avgScore = graded.length ? Math.round(graded.reduce((a, s) => a + Number(s.score), 0) / graded.length) : 0;
  const completion = course.progress != null
    ? Number(course.progress)
    : (enrollees.length ? Math.round(enrollees.reduce((a, s) => a + (Number(s.progress) || 0), 0) / enrollees.length) : 0);

  const courseName = course.courseName || course.name || 'Course';
  const codeShort = course.code ? String(course.code).replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase() : initials(courseName);
  const isActive = course.active === 1 || course.status === 'active';

  const kpis = [
    { label: 'Learners', value: studentsCount, icon: 'enroll', help: COURSE_DETAIL_KPI_HELP.learners },
    { label: 'Assessments', value: assessments.length, icon: 'assess', help: COURSE_DETAIL_KPI_HELP.assessments },
    { label: 'Avg. completion', value: `${completion}%`, icon: 'target', help: COURSE_DETAIL_KPI_HELP.avgCompletion },
    { label: 'Avg. score', value: `${avgScore}%`, icon: 'award', help: COURSE_DETAIL_KPI_HELP.avgScore },
  ];

  const sortedAssessments = useMemo(
    () => sortAssessmentsLocally(assessments, assessmentSortBy, assessmentSortDir),
    [assessments, assessmentSortBy, assessmentSortDir],
  );
  const sortedEnrollees = useMemo(
    () => sortEnrolleesLocally(enrollees, enrolleeSortBy, enrolleeSortDir),
    [enrollees, enrolleeSortBy, enrolleeSortDir],
  );
  const sortedTrainings = useMemo(
    () => sortCourseTrainingsLocally(trainings, trainingSortBy, trainingSortDir),
    [trainings, trainingSortBy, trainingSortDir],
  );
  const sortedGradebook = useMemo(
    () => sortEnrolleesLocally(enrollees, gradebookSortBy, gradebookSortDir),
    [enrollees, gradebookSortBy, gradebookSortDir],
  );

  const tabs = useMemo(() => {
    const all = [
      ['overview', 'Overview'],
      ['analysis', 'Analysis'],
      ['training', `Training · ${trainings.length}`],
      ['assessments', `Assessments · ${assessments.length}`],
      ['enrollees', `Learners · ${studentsCount}`],
      ['materials', `Materials · ${materials.length}`],
      ['gradebook', 'GradeBook'],
    ];
    return analysisHidden ? all.filter(([key]) => key !== 'analysis') : all;
  }, [analysisHidden, trainings.length, assessments.length, studentsCount, materials.length]);

  useEffect(() => {
    if (tab === 'analysis' && analysisHidden) {
      setTab('overview');
    }
  }, [tab, analysisHidden]);

  const closeStudentDialog = () => {
    setStudentOpen(false);
    setStudentData(studentInitial);
  };

  const openAddStudent = () => {
    if (!guardAction('action.course.detail.enrollee.add')) return;
    setStudentData({
      ...studentInitial,
      courseId,
      courseName,
    });
    setStudentOpen(true);
  };

  const openUpdateStudent = (student) => {
    if (!guardAction('action.course.detail.enrollee.edit')) return;
    setStudentData({
      ...studentInitial,
      ...student,
      courseId: student.courseId || courseId,
      courseName: student.courseName || courseName,
      studentEmail: student.studentEmail || student.email || '',
    });
    setStudentOpen(true);
  };

  const onStudentChange = (e) => {
    const { id, value } = e.target;
    setStudentData((current) => ({ ...current, [id]: value }));
  };

  const submitStudent = async () => {
    const featureKey = studentData.id
      ? 'action.course.detail.enrollee.edit'
      : 'action.course.detail.enrollee.add';
    if (!guardAction(featureKey)) return;
    setSavingStudent(true);
    const userId = localStorage.getItem('userId');
    try {
      const isUpdate = Boolean(studentData.id);
      await axios({
        method: isUpdate ? 'put' : 'post',
        url: isUpdate
          ? `${apiUrl}/api/studentcourses/${userId}&${studentData.studentId}`
          : `${apiUrl}/api/studentcourses/${userId}`,
        data: studentData,
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });
      closeStudentDialog();
      fetchDetails(false);
    } catch (err) {
      console.error('Error saving enrollee:', err);
      alert('Failed to save student enrollment.');
    } finally {
      setSavingStudent(false);
    }
  };

  const deleteStudent = async (student) => {
    if (!guardAction('action.course.detail.enrollee.delete')) return;
    const rowId = student && student.id;
    if (!rowId) { alert('Missing enrollment id for delete.'); return; }
    const confirmed = await askConfirmation({
      title: 'Remove student?',
      message: 'Are you sure you want to delete this student from the course?',
      confirmLabel: 'Remove',
      variant: 'danger',
    });
    if (!confirmed) return;
    const userId = localStorage.getItem('userId');
    try {
      await axios.delete(`${apiUrl}/api/studentcourses/${userId}&${rowId}`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });
      fetchDetails(false);
    } catch (err) {
      console.error('Error deleting enrollee:', err);
      alert('Failed to delete student enrollment.');
    }
  };

  const openUpdateAssessment = (assessment) => {
    if (!guardAction('action.course.detail.assessment.edit')) return;
    setSelectedAssessment(assessment);
    setAssessmentEditOpen(true);
  };

  const closeUpdateAssessment = () => {
    setAssessmentEditOpen(false);
    setSelectedAssessment(null);
    fetchDetails(false);
  };

  const openAssessmentSettings = (assessment) => {
    if (!guardAction('action.course.detail.assessment.settings')) return;
    setAssessmentSettingsTarget({ ...assessment, courseName });
    setAssessmentSettingsOpen(true);
  };

  const closeAssessmentSettings = () => {
    setAssessmentSettingsOpen(false);
    setAssessmentSettingsTarget(null);
  };

  const deleteAssessment = async (assessment) => {
    if (!guardAction('action.course.detail.assessment.delete')) return;
    const testId = assessment && assessment.testId;
    if (!testId) { alert('Missing assessment id for delete.'); return; }
    const confirmed = await askConfirmation({
      title: 'Delete assessment?',
      message: 'Are you sure you want to delete this assessment?',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await axios.post(`${apiUrl}/api/Assessment/deleteTest/${testId}`, null, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });
      fetchDetails(false);
    } catch (err) {
      console.error('Error deleting assessment:', err);
      alert('Failed to delete assessment.');
    }
  };

  if (loading) {
    return (
      <>
        <DashboardNavbar menuItems={[]} />
        <ShimmerWrap>
          {/* back button */}
          <Skeleton $w="110px" $h="30px" $r="12px" />

          {/* hero card */}
          <Card $pad style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            <Skeleton $w="60px" $h="60px" $r="16px" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <Skeleton $w="240px" $h="22px" $r="8px" />
                <Skeleton $w="60px" $h="20px" $r="999px" />
              </div>
              <Skeleton $w="160px" $h="13px" $r="8px" />
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <Skeleton $w="90px" $h="24px" $r="999px" />
                <Skeleton $w="70px" $h="24px" $r="999px" />
                <Skeleton $w="140px" $h="24px" $r="999px" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Skeleton $w="72px" $h="30px" $r="12px" />
              <Skeleton $w="120px" $h="30px" $r="12px" />
              <Skeleton $w="130px" $h="30px" $r="12px" />
            </div>
          </Card>

          {/* kpi cards */}
          <StatGrid>
            {[1, 2, 3, 4].map((i) => (
              <Stat key={i}>
                <StatTop><Skeleton $w="34px" $h="34px" $r="10px" /></StatTop>
                <Skeleton $w="70px" $h="11px" $r="6px" $mb="8px" style={{ marginTop: 10 }} />
                <Skeleton $w="50px" $h="26px" $r="6px" />
              </Stat>
            ))}
          </StatGrid>

          {/* tabs */}
          <div style={{ display: 'flex', gap: 8 }}>
            {[100, 80, 110, 90, 80, 80].map((w, i) => (
              <Skeleton key={i} $w={`${w}px`} $h="32px" $r="8px" />
            ))}
          </div>

          {/* content area */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 16 }}>
            <Card $pad style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Skeleton $w="140px" $h="16px" $r="8px" $mb="4px" />
              <Skeleton $h="11px" $r="6px" />
              <Skeleton $h="11px" $r="6px" $w="90%" />
              <Skeleton $h="11px" $r="6px" $w="80%" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton key={i} $h="52px" $r="12px" />
                ))}
              </div>
            </Card>
            <Card $pad style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <Skeleton $w="130px" $h="16px" $r="8px" style={{ alignSelf: 'flex-start' }} />
              <Skeleton $circle $h="150px" style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', gap: 22 }}>
                <Skeleton $w="40px" $h="26px" $r="6px" />
                <Skeleton $w="40px" $h="26px" $r="6px" />
              </div>
            </Card>
          </div>
        </ShimmerWrap>
      </>
    );
  }

  return (
    <div className="route-enter">
      <DashboardNavbar menuItems={[]} />

      <Btn $variant="ghost" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
        <Icon name="chevL" size={15} />All courses
      </Btn>

      {/* hero */}
      <Hero>
        <RowIco style={{ width: 60, height: 60, borderRadius: 16, fontSize: 18, background: theme.accentSoft, color: theme.accentStrong }}>{codeShort}</RowIco>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div className="hero__title">
            <h2>{courseName}</h2>
            <Tag $tone={isActive ? 'green' : 'gray'}><TagDot />{isActive ? 'Active' : 'Archived'}</Tag>
          </div>
          <div className="hero__sub">{course.code || '—'} · {course.vendor || '—'}</div>
          <div className="hero__chips">
            {course.type && <Chip $soft>{course.type}</Chip>}
            {course.level && <Chip $soft>{course.level}</Chip>}
            <Chip $soft><Icon name="calendar" size={13} />{fmtDate(course.start)} – {fmtDate(course.expire)}</Chip>
          </div>
        </div>
        <div className="hero__actions">
          <PageActionsMenu
            primaryLabel="Add assessment"
            primaryIcon={<Plus size={16} strokeWidth={2.25} aria-hidden="true" />}
            onPrimaryClick={() => guardAction('action.course.detail.addAssessment', () => setAssessmentCreateOpen(true))}
            primaryLocked={isLocked('action.course.detail.addAssessment')}
            menuTitle="Course tools"
            menuSubtitle="Edit settings and run analysis."
          >
            {(closeActions) => (
              <ActionsSection label="Manage">
                <ActionsMenuItem
                  label="Edit course"
                  description="Name, code, vendor and domains"
                  icon={<Pencil size={15} strokeWidth={1.9} />}
                  closeActions={closeActions}
                  onClick={openEditCourse}
                  {...lockProps('action.course.detail.editCourse')}
                />
                <ActionsMenuItem
                  label="Edit settings"
                  description="Schedule, pricing, visibility and registration"
                  icon={<Settings size={15} strokeWidth={1.9} />}
                  closeActions={closeActions}
                  onClick={() => guardAction('action.course.detail.editSettings', () => navigate('/Portal/Instructor/CourseSetting', { state: { courseDetails: course } }))}
                  {...lockProps('action.course.detail.editSettings')}
                />
                <ActionsMenuItem
                  label="Analyze course"
                  description="AI insights and weak-area analysis"
                  icon={<Sparkles size={15} strokeWidth={1.9} />}
                  closeActions={closeActions}
                  onClick={() => guardAction('action.yaki.analyzeCourse', () => setTab('analysis'))}
                  {...lockProps('action.yaki.analyzeCourse', () => guardAction('action.yaki.analyzeCourse'))}
                  locked={analysisLocked}
                />
              </ActionsSection>
            )}
          </PageActionsMenu>
        </div>
      </Hero>

      {/* kpis */}
      <StatGrid style={{ marginTop: '10px' }}>
        {kpis.map((k) => (
          <Stat key={k.label}>
            <StatTop><StatIco><Icon name={k.icon} /></StatIco></StatTop>
            <StatLabel><LabelWithHelp help={k.help}>{k.label}</LabelWithHelp></StatLabel>
            <StatValue className="tnum">{k.value}</StatValue>
          </Stat>
        ))}
      </StatGrid>

      {/* tabs */}
      <SetTabs style={{ marginTop: 16 }}>
        {tabs.map(([k, l]) => <SetTab key={k} $on={tab === k} onClick={() => setTab(k)}>{l}</SetTab>)}
      </SetTabs>

      {tab === 'overview' && (
        <ChartGrid style={{ gridTemplateColumns: '1.5fr 1fr' }}>
          <Card $pad>
            <SectionTitle>About this course</SectionTitle>
            <Muted as="p" style={{ margin: 0, fontSize: 13, lineHeight: 1.6, display: 'block' }}>
              {course.description
                || `${courseName} equips learners with the core knowledge and applied skills for ${String(course.level || 'professional').toLowerCase()}-level practice. Delivered by ${course.vendor || 'the provider'}, it combines structured modules, case studies and ${assessments.length} graded ${assessments.length === 1 ? 'assessment' : 'assessments'} to certify competency.`}
            </Muted>
            <MetaGrid>
              {[
                ['Vendor', course.vendor || '—'],
                ['Type', course.type || '—'],
                ['Level', course.level || '—'],
                ['Starts', fmtDate(course.start)],
                ['Expires', fmtDate(course.expire)],
                ['Learners', `${studentsCount} people`],
              ].map(([l, v]) => (
                <MetaCell key={l}>
                  <div className="meta__k">{l}</div>
                  <div className="meta__v">{v}</div>
                </MetaCell>
              ))}
            </MetaGrid>
          </Card>
          <Card $pad style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 6 }}>
            <SectionTitle style={{ alignSelf: 'flex-start' }}>Class completion</SectionTitle>
            <Donut value={completion} total={100} size={150} stroke={16} label="complete" />
            <div style={{ display: 'flex', gap: 22, marginTop: 10 }}>
              <div><StatValue className="tnum" style={{ fontSize: 22 }}>{studentsCount}</StatValue><Muted style={{ fontSize: 10 }}>enrolled</Muted></div>
              <div><StatValue className="tnum" style={{ fontSize: 22 }}>{avgScore}%</StatValue><Muted style={{ fontSize: 10 }}>avg score</Muted></div>
            </div>
          </Card>
          {courseDomains.length > 0 && (
            <Card $pad style={{ gridColumn: '1 / -1' }}>
              <SectionTitle>Domains &amp; subdomains</SectionTitle>
              {courseDomains.map((d, i) => (
                <div key={d.domainId ?? i} style={{ padding: '9px 0', borderTop: i ? '1px solid var(--c-border, rgba(17,20,38,.10))' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, fontWeight: 650 }}>
                    <span>{d.name}</span>
                    {d.percentage != null && d.percentage !== '' && <span style={{ fontSize: 11, fontWeight: 700 }}>{d.percentage}%</span>}
                  </div>
                  {(d.subDomain || []).length > 0 && (
                    <ul style={{ margin: '6px 0 0', paddingLeft: 16, fontSize: 11.5, lineHeight: 1.5 }}>
                      {d.subDomain.map((s, k) => (
                        <li key={s.subDomainId ?? k}>{s.name}{s.subTopics ? ` — ${s.subTopics}` : ''}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </Card>
          )}
        </ChartGrid>
      )}

      {tab === 'analysis' && (
        <CourseAnalysis
          courseId={resolvedCourseId}
          course={course}
          enrollees={enrollees}
          assessments={assessments}
        />
      )}

      {tab === 'assessments' && (
        <DataTable
          rows={sortedAssessments}
          rowKey={(a, i) => a.testId ?? a.id ?? i}
          sortBy={assessmentSortBy}
          sortDir={assessmentSortDir}
          onSortChange={handleAssessmentSortChange}
          onRowClick={(a) => navigate('/Portal/Instructor/AssessmentDetail', { state: { assessment: { ...a, courseName } } })}
          searchExtra={
            <Muted style={{ fontSize: 11.5, fontWeight: 600 }}>
              {assessments.length} assessment{assessments.length === 1 ? '' : 's'} in this course
            </Muted>
          }
          toolbarRight={
            <Btn
              $variant="primary"
              $sm
              onClick={() => guardAction('action.course.detail.addAssessment', () => setAssessmentCreateOpen(true))}
            >
              <Icon name="plus" size={15} />New assessment
            </Btn>
          }
          columns={[
            {
              header: 'Assessment',
              sortKey: ASSESSMENT_SORT_FIELDS.name,
              render: (a) => <CellPrimary>{a.name}</CellPrimary>,
            },
            {
              header: 'Questions',
              sortKey: ASSESSMENT_SORT_FIELDS.questions,
              render: (a) => a.questions ?? '—',
              mono: true,
            },
            {
              header: 'Duration',
              sortKey: ASSESSMENT_SORT_FIELDS.duration,
              render: (a) => (a.duration ? `${a.duration}m` : '—'),
              muted: true,
              mono: true,
            },
            {
              header: <LabelWithHelp help={TABLE_HELP.submissions}>Submissions</LabelWithHelp>,
              sortKey: ASSESSMENT_SORT_FIELDS.submissions,
              render: (a) => {
                const assigned = Number(a.assigned) || 0;
                const submitted = Number(a.submitted) || 0;
                return (
                  <MiniProg>
                    <div className="track"><i style={{ width: `${assigned ? (submitted / assigned) * 100 : 0}%` }} /></div>
                    <span className="tnum">{submitted}/{assigned}</span>
                  </MiniProg>
                );
              },
            },
            {
              header: <LabelWithHelp help={TABLE_HELP.avgScore}>Avg. score</LabelWithHelp>,
              sortKey: ASSESSMENT_SORT_FIELDS.avg,
              render: (a) => (a.avg ? `${a.avg}%` : '—'),
              mono: true,
            },
            {
              header: <LabelWithHelp help={TABLE_HELP.assessmentStatus}>Status</LabelWithHelp>,
              sortKey: ASSESSMENT_SORT_FIELDS.status,
              render: (a) => {
                const status = a.status || (a.active === 1 ? 'current' : 'closed');
                const [tone, lbl] = status === 'current' ? ['green', 'Live'] : status === 'pending' ? ['amber', 'Upcoming'] : ['gray', 'Closed'];
                return <Tag $tone={tone}><TagDot />{lbl}</Tag>;
              },
            },
          ]}
          actionsColumnWidth={52}
          actions={(a, i) => (
            <GroupedRowActionSheet
              entityLabel="Assessment"
              title={a.name}
              menuAriaLabel="Assessment actions"
              layoutKey={String(a.testId ?? a.id ?? i)}
              primary={aiBuilderEnabled ? {
                key: 'generate',
                icon: <Sparkles {...ICON_PROPS} />,
                label: 'Generate questions',
                ...lockProps('action.course.detail.assessment.generate'),
              } : null}
              groupLabel="For this assessment"
              groupItems={[
                {
                  key: 'settings',
                  icon: <Settings {...ICON_PROPS} />,
                  label: 'Assessment settings',
                  ...lockProps('action.course.detail.assessment.settings'),
                },
                {
                  key: 'review',
                  icon: <ClipboardList {...ICON_PROPS} />,
                  label: 'Review questions',
                  ...lockProps('action.course.detail.assessment.review'),
                },
              ]}
              items={[
                {
                  key: 'edit',
                  icon: <Pencil {...ICON_PROPS} />,
                  label: 'Edit',
                  ...lockProps('action.course.detail.assessment.edit'),
                },
              ]}
              dangerItem={{
                key: 'delete',
                icon: <Trash2 {...ICON_PROPS} />,
                label: 'Delete',
                ...lockProps('action.course.detail.assessment.delete'),
              }}
              onAction={(key) => {
                if (key === 'generate') {
                  openGenerateForAssessment(a);
                } else if (key === 'settings') {
                  openAssessmentSettings(a);
                } else if (key === 'review') {
                  navigate('/Portal/Instructor/Questions', {
                    state: { testId: a.testId, fromLabel: a.name },
                  });
                } else if (key === 'edit') openUpdateAssessment(a);
                else if (key === 'delete') deleteAssessment(a);
              }}
            />
          )}
          emptyText="No assessments yet. Create one or generate questions with AI."
        />
      )}

      {tab === 'enrollees' && (
        <DataTable
          rows={sortedEnrollees}
          rowKey={(s, i) => s.id ?? s.studentId ?? i}
          sortBy={enrolleeSortBy}
          sortDir={enrolleeSortDir}
          onSortChange={handleEnrolleeSortChange}
          searchExtra={
            <Muted style={{ fontSize: 11.5, fontWeight: 600 }}>
              Showing {enrollees.length} of {studentsCount} enrollees
            </Muted>
          }
          toolbarRight={
            <Btn $variant="ghost" $sm onClick={openAddStudent}>
              <Icon name="plus" size={15} />Add student
            </Btn>
          }
          columns={[
            {
              header: 'Name',
              sortKey: ENROLLEE_SORT_FIELDS.name,
              render: (s) => {
                const email = s.studentEmail || s.email || '';
                const name = s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim() || email;
                return (
                  <CellFlex>
                    <Av style={{ background: s.color || 'oklch(0.55 0.21 274)' }}>{initials(name)}</Av>
                    <div><CellPrimary>{name}</CellPrimary><CellSub>{email}</CellSub></div>
                  </CellFlex>
                );
              },
            },
            {
              header: 'Enrolled',
              sortKey: ENROLLEE_SORT_FIELDS.enrolled,
              render: (s) => fmtDate(s.enrolled),
              muted: true,
              mono: true,
            },
            {
              header: <LabelWithHelp help={TABLE_HELP.progress}>Progress</LabelWithHelp>,
              sortKey: ENROLLEE_SORT_FIELDS.progress,
              render: (s) => <CourseProgressCell row={s} />,
            },
            {
              header: <LabelWithHelp help={TABLE_HELP.latestScore}>Latest score</LabelWithHelp>,
              sortKey: ENROLLEE_SORT_FIELDS.score,
              render: (s) => (s.score ? `${s.score}%` : '—'),
              mono: true,
            },
            {
              header: <LabelWithHelp help={TABLE_HELP.learnerStatus}>Status</LabelWithHelp>,
              sortKey: ENROLLEE_SORT_FIELDS.status,
              render: (s) => {
                const rowStatus = s.status || (s.activestatus === 0 ? 'inactive' : 'active');
                const [tone, lbl] = STATUS_TAG[rowStatus] || STATUS_TAG.active;
                return <Tag $tone={tone}><TagDot />{lbl}</Tag>;
              },
            },
          ]}
          actionsColumnWidth={52}
          actions={(s, i) => {
            const email = s.studentEmail || s.email || '';
            const name = s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim() || email;
            return (
              <GroupedRowActionSheet
                entityLabel="Learner"
                title={name}
                menuAriaLabel="Learner actions"
                layoutKey={String(s.id ?? s.studentId ?? i)}
                groupLabel="For this learner"
                groupItems={[
                  {
                    key: 'edit',
                    icon: <Pencil {...ICON_PROPS} />,
                    label: 'Edit',
                    ...lockProps('action.course.detail.enrollee.edit'),
                  },
                ]}
                dangerItem={{
                  key: 'delete',
                  icon: <Trash2 {...ICON_PROPS} />,
                  label: 'Delete',
                  ...lockProps('action.course.detail.enrollee.delete'),
                }}
                onAction={(key) => {
                  if (key === 'edit') openUpdateStudent(s);
                  else if (key === 'delete') deleteStudent(s);
                }}
              />
            );
          }}
          emptyText="No enrollees yet."
        />
      )}

      {tab === 'materials' && (
        <>
          <TblToolbar style={{ marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
            <div className="search" style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${theme.border}`, borderRadius: theme.rMd, padding: '0 10px', background: theme.surface2, height: 34 }}>
              <Icon name="eye" size={14} />
              <input
                placeholder="Search materials…"
                value={materialSearch}
                onChange={(e) => setMaterialSearch(e.target.value)}
                style={{ border: 'none', outline: 'none', background: 'transparent', color: theme.text, fontSize: 13, height: 32, minWidth: 160 }}
              />
            </div>
            <div style={{ flex: 1 }} />
            <input
              placeholder="Paste a material URL…"
              value={materialUrl}
              onChange={(e) => setMaterialUrl(e.target.value)}
              style={{ border: `1px solid ${theme.border}`, borderRadius: theme.rMd, padding: '0 10px', background: theme.surface2, color: theme.text, fontSize: 13, height: 34, minWidth: 200 }}
            />
            <Btn $variant="ghost" $sm onClick={handleAddMaterialUrl}><Icon name="plus" size={15} />Add URL</Btn>
            <Btn
              $variant="ghost"
              $sm
              onClick={() => guardAction('action.course.detail.material.upload', () => setUploadOpen(true))}
            >
              <Icon name="upload" size={15} />Upload file
            </Btn>
          </TblToolbar>
          <Grid2>
            {materials
              .filter((m) => !materialSearch.trim() || String(m.name || '').toLowerCase().includes(materialSearch.trim().toLowerCase()))
              .map((m, i) => (
                <Tile key={m.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <KpiIco style={{ background: theme.accentSoft, color: theme.accentStrong }}><Icon name="doc" /></KpiIco>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 650, fontSize: 14 }}>{m.name}</div>
                    <Muted style={{ fontSize: 10, marginTop: 3, display: 'block' }}>
                      {[m.type, m.pages ? `${m.pages} pages` : null, m.size].filter(Boolean).join(' · ')}
                    </Muted>
                  </div>
                  <GroupedRowActionSheet
                    entityLabel="Material"
                    title={m.name}
                    menuAriaLabel="Material actions"
                    layoutKey={String(m.id ?? i)}
                    groupLabel="For this material"
                    groupItems={[
                      {
                        key: 'analyze',
                        icon: <Sparkles {...ICON_PROPS} />,
                        label: 'Re-analyze',
                        ...lockProps('action.course.detail.material.reanalyze'),
                      },
                      ...(m.url
                        ? [
                            {
                              key: 'view',
                              icon: <Eye {...ICON_PROPS} />,
                              label: 'View',
                              ...lockProps('action.course.detail.material.view'),
                            },
                            {
                              key: 'download',
                              icon: <Download {...ICON_PROPS} />,
                              label: 'Download',
                              ...lockProps('action.course.detail.material.download'),
                            },
                          ]
                        : []),
                    ]}
                    onAction={(key) => {
                      if (key === 'analyze') handleReanalyzeMaterial(m);
                      else if (key === 'view' && m.url) window.open(m.url, '_blank', 'noopener,noreferrer');
                      else if (key === 'download' && m.url) {
                        const link = document.createElement('a');
                        link.href = m.url;
                        link.download = '';
                        link.rel = 'noopener noreferrer';
                        link.click();
                      }
                    }}
                  />
                </Tile>
              ))}
            <UploadTile onClick={() => guardAction('action.course.detail.material.upload', () => setUploadOpen(true))}>
              <Icon name="upload" size={18} />Upload PDF material
            </UploadTile>
          </Grid2>
        </>
      )}

      {tab === 'training' && (
        <DataTable
          rows={sortedTrainings}
          rowKey={(t, i) => t.trainingId ?? i}
          sortBy={trainingSortBy}
          sortDir={trainingSortDir}
          onSortChange={handleTrainingSortChange}
          searchExtra={
            <Muted style={{ fontSize: 11.5, fontWeight: 600 }}>
              {trainings.length} training{trainings.length === 1 ? '' : 's'} tied to this course
            </Muted>
          }
          toolbarRight={
            <Btn
              $variant="primary"
              $sm
              onClick={() => guardAction('action.course.detail.training.new', () => navigate('/Portal/Instructor/TrainingSection'))}
            >
              <Icon name="plus" size={15} />New training
            </Btn>
          }
          columns={[
            {
              header: 'Training',
              sortKey: COURSE_TRAINING_SORT_FIELDS.name,
              render: (t) => <CellPrimary>{t.trainingName}</CellPrimary>,
            },
            {
              header: 'Start',
              sortKey: COURSE_TRAINING_SORT_FIELDS.start,
              render: (t) => fmtDate(t.startDate),
              muted: true,
              mono: true,
            },
            {
              header: <LabelWithHelp help={TRAINING_TABLE_HELP.due}>Due</LabelWithHelp>,
              sortKey: COURSE_TRAINING_SORT_FIELDS.due,
              render: (t) => fmtDate(t.completionDate),
              muted: true,
              mono: true,
            },
            {
              header: <LabelWithHelp help={TRAINING_TABLE_HELP.completion}>Completion</LabelWithHelp>,
              sortKey: COURSE_TRAINING_SORT_FIELDS.completion,
              render: (t) => {
                const pct = Number(t.completionPercent) || 0;
                const done = Number(t.completedStudents) || 0;
                const assigned = Number(t.assignedStudents) || 0;
                return (
                  <MiniProg title={`${pct}% of enrolled learners`}>
                    <div className="track"><i style={{ width: `${pct}%` }} /></div>
                    <span className="tnum">{assigned ? `${done}/${assigned}` : '—'}</span>
                  </MiniProg>
                );
              },
            },
            {
              header: <LabelWithHelp help={TRAINING_TABLE_HELP.domains}>Domains</LabelWithHelp>,
              sortKey: COURSE_TRAINING_SORT_FIELDS.domains,
              render: (t) => (t.domains || []).length,
              mono: true,
            },
            {
              header: <LabelWithHelp help={TRAINING_TABLE_HELP.questions}>Questions</LabelWithHelp>,
              sortKey: COURSE_TRAINING_SORT_FIELDS.questions,
              render: (t) => (t.questions || []).length,
              mono: true,
            },
            {
              header: <LabelWithHelp help={TRAINING_TABLE_HELP.status}>Status</LabelWithHelp>,
              sortKey: COURSE_TRAINING_SORT_FIELDS.status,
              render: (t) => {
                const tActive = t.active === 1;
                return <StatusBadge $active={tActive}>{tActive ? 'Active' : 'Inactive'}</StatusBadge>;
              },
            },
          ]}
          actionsColumnWidth={52}
          actions={(t, i) => (
            <GroupedRowActionSheet
              entityLabel="Training"
              title={t.trainingName}
              menuAriaLabel="Training actions"
              layoutKey={String(t.trainingId ?? i)}
              groupLabel="For this training"
              groupItems={[
                {
                  key: 'settings',
                  icon: <Settings {...ICON_PROPS} />,
                  label: 'Training settings',
                  ...lockProps('action.course.detail.training.settings'),
                },
                ...(t.videoUrl
                  ? [{
                    key: 'video',
                    icon: <Video {...ICON_PROPS} />,
                    label: 'Watch video',
                    ...lockProps('action.course.detail.training.video'),
                  }]
                  : []),
              ]}
              onAction={(key) => {
                if (key === 'settings') {
                  navigate('/Portal/Instructor/TrainingSetting', {
                    state: { trainingDetails: t },
                  });
                } else if (key === 'video' && t.videoUrl) {
                  setTrainingVideoUrl(t.videoUrl);
                  setTrainingVideoOpen(true);
                }
              }}
            />
          )}
          emptyText="No trainings yet for this course."
        />
      )}

      {tab === 'gradebook' && (
        <DataTable
          rows={sortedGradebook}
          rowKey={(s, i) => s.id ?? s.studentId ?? i}
          sortBy={gradebookSortBy}
          sortDir={gradebookSortDir}
          onSortChange={handleGradebookSortChange}
          searchExtra={
            <Muted style={{ fontSize: 11.5, fontWeight: 600 }}>
              Scores & progress for {enrollees.length} learner{enrollees.length === 1 ? '' : 's'}
            </Muted>
          }
          columns={[
            {
              header: 'Learner',
              sortKey: ENROLLEE_SORT_FIELDS.name,
              render: (s) => {
                const email = s.studentEmail || s.email || '';
                const name = s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim() || email;
                return (
                  <CellFlex>
                    <Av style={{ background: s.color || 'oklch(0.55 0.21 274)' }}>{initials(name)}</Av>
                    <div><CellPrimary>{name}</CellPrimary><CellSub>{email}</CellSub></div>
                  </CellFlex>
                );
              },
            },
            {
              header: <LabelWithHelp help={TABLE_HELP.progress}>Progress</LabelWithHelp>,
              sortKey: ENROLLEE_SORT_FIELDS.progress,
              render: (s) => <CourseProgressCell row={s} />,
            },
            // Only worth a column when the course actually has trainings; otherwise it
            // would be a permanently empty bar on every row.
            ...(trainings.length
              ? [{
                header: <LabelWithHelp help={TABLE_HELP.trainingProgress}>Training</LabelWithHelp>,
                sortKey: ENROLLEE_SORT_FIELDS.trainingProgress,
                render: (s) => {
                  const pct = Number(s.trainingProgress) || 0;
                  return (
                    <MiniProg>
                      <div className="track"><i style={{ width: `${pct}%` }} /></div>
                      <span className="tnum">{pct}%</span>
                    </MiniProg>
                  );
                },
              }]
              : []),
            {
              header: <LabelWithHelp help={TABLE_HELP.latestScore}>Latest score</LabelWithHelp>,
              sortKey: ENROLLEE_SORT_FIELDS.score,
              render: (s) => (s.score ? `${s.score}%` : '—'),
              mono: true,
            },
            {
              header: <LabelWithHelp help={TABLE_HELP.learnerStatus}>Status</LabelWithHelp>,
              sortKey: ENROLLEE_SORT_FIELDS.status,
              render: (s) => {
                const rowStatus = s.status || (s.activestatus === 0 ? 'inactive' : 'active');
                const [tone, lbl] = STATUS_TAG[rowStatus] || STATUS_TAG.active;
                return <Tag $tone={tone}><TagDot />{lbl}</Tag>;
              },
            },
          ]}
          emptyText="No enrollees to grade yet."
        />
      )}

      <UploadPDF
        courseId={courseId}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />
      <AssessmentDialog
        open={assessmentCreateOpen}
        handleClose={() => setAssessmentCreateOpen(false)}
        initialCourse={course}
        lockCourse
        onCreated={() => fetchDetails(false)}
      />
      {assessmentEditOpen && (
        <AssessmentFormDialog
          open={assessmentEditOpen}
          handleClose={closeUpdateAssessment}
          data={selectedAssessment || {}}
          testId={selectedAssessment && selectedAssessment.testId}
        />
      )}
      {generateOpen && generateAssessment && (
        <GenerateQuestiondialog
          open={generateOpen}
          handleClose={() => { setGenerateOpen(false); setGenerateAssessment(null); }}
          data={generateAssessment}
          testId={generateAssessment.testId}
        />
      )}
      <AssessmentSettingDialog
        open={assessmentSettingsOpen}
        testDetails={assessmentSettingsTarget}
        onClose={closeAssessmentSettings}
        saveLocked={isLocked('action.course.detail.assessment.settings')}
        onSaveBlocked={() => setLockedDialogOpen(true)}
        onSaved={() => {
          fetchDetails(false);
          closeAssessmentSettings();
        }}
      />
      <TrainingVideoDialog
        open={trainingVideoOpen}
        videoUrl={trainingVideoUrl}
        onClose={() => { setTrainingVideoOpen(false); setTrainingVideoUrl(null); }}
      />
      <StudentFormDialog
        open={studentOpen}
        handleClose={closeStudentDialog}
        data={studentData}
        onChange={onStudentChange}
        handleFormSubmit={submitStudent}
        loading={savingStudent}
        lockCourse
      />
      {editCourseData && (
        <CourseEditDialog
          open={editCourseOpen}
          handleClose={() => setEditCourseOpen(false)}
          data={editCourseData}
          onChange={onEditCourseChange}
          handleFormSubmit={handleEditCourseSubmit}
          loading={editCourseLoading}
          saveFeatureKey="action.course.detail.editCourse"
        />
      )}
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </div>
  );
};

export default CourseDetail;
